import React, { useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useToast } from '../contexts/ToastProvider';
import { CheckCircleIcon, XCircleIcon, PowerIcon, EyeIcon, CopyIcon, ExternalLinkIcon, RefreshCwIcon, ChevronDownIcon, ChevronRightIcon, AlertCircleIcon } from 'lucide-react';
interface Exercise {
  id: string;
  exerciseId: string;
  title: string;
  status: 'active' | 'inactive';
  publishedAt: string;
  activationToken: string;
  locales: string[];
  completions: number;
}
export function ManageExercises() {
  const {
    t
  } = useOrganization();
  const {
    showToast
  } = useToast();
  const [expandedExercise, setExpandedExercise] = useState<string | null>(null);
  // Mock data - in production this would come from an API
  const [exercises, setExercises] = useState<Exercise[]>([{
    id: '1',
    exerciseId: 'EX-EMO-UK-2025-10-v1',
    title: 'Emotional State Assessment',
    status: 'active',
    publishedAt: '2025-01-15T10:30:00Z',
    activationToken: 'act_kmg_2025_ex_emo_uk_10_v1_a1b2c3d4',
    locales: ['EN', 'UK'],
    completions: 1234
  }, {
    id: '2',
    exerciseId: 'EX-STRESS-UK-2025-01-v1',
    title: 'Stress Level Assessment',
    status: 'active',
    publishedAt: '2025-01-10T14:20:00Z',
    activationToken: 'act_kmg_2025_ex_stress_uk_01_v1_x9y8z7w6',
    locales: ['EN', 'UK', 'FR'],
    completions: 856
  }, {
    id: '3',
    exerciseId: 'EX-WELLBEING-UK-2024-12-v2',
    title: 'Wellbeing Check-In',
    status: 'inactive',
    publishedAt: '2024-12-20T09:15:00Z',
    activationToken: 'act_kmg_2024_ex_wellbeing_uk_12_v2_p5q4r3s2',
    locales: ['EN', 'UK'],
    completions: 2145
  }]);
  const handleToggleStatus = (exerciseId: string) => {
    setExercises(exercises.map(ex => ex.id === exerciseId ? {
      ...ex,
      status: ex.status === 'active' ? 'inactive' : 'active'
    } : ex));
    const exercise = exercises.find(ex => ex.id === exerciseId);
    showToast('success', `Exercise ${exercise?.status === 'active' ? 'deactivated' : 'activated'}`);
  };
  const handleRegenerateToken = (exerciseId: string) => {
    const newToken = `act_kmg_${Date.now()}_${Math.random().toString(36).substring(7)}`;
    setExercises(exercises.map(ex => ex.id === exerciseId ? {
      ...ex,
      activationToken: newToken
    } : ex));
    showToast('success', 'Activation token regenerated');
  };
  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    showToast('success', `${label} copied to clipboard`);
  };
  const getDirectUrl = (exercise: Exercise) => {
    return `https://runner.agilebrain.io/exercise/${exercise.exerciseId}?token=${exercise.activationToken}`;
  };
  const getEmbedCode = (exercise: Exercise) => {
    const directUrl = getDirectUrl(exercise);
    return `<iframe 
  src="${directUrl}" 
  width="100%" 
  height="800" 
  frameborder="0" 
  allow="fullscreen"
  title="AgileBrain Assessment">
</iframe>`;
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <div className="p-8" data-id="element-1643">
      <div className="mb-8" data-id="element-1644">
        <h1 className="text-3xl font-semibold text-gray-900" data-id="element-1645">
          Manage Exercises
        </h1>
        <p className="text-gray-600 mt-1" data-id="element-1646">
          View and manage all published exercises, embed codes, and activation
          tokens
        </p>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200" data-id="element-1647">
        {/* Header */}
        <div className="grid grid-cols-12 gap-4 p-4 border-b border-gray-200 bg-gray-50 font-medium text-sm text-gray-700" data-id="element-1648">
          <div className="col-span-4" data-id="element-1649">Exercise</div>
          <div className="col-span-2" data-id="element-1650">Status</div>
          <div className="col-span-2" data-id="element-1651">Locales</div>
          <div className="col-span-2" data-id="element-1652">Completions</div>
          <div className="col-span-2" data-id="element-1653">Published</div>
        </div>
        {/* Exercise List */}
        <div className="divide-y divide-gray-200" data-id="element-1654">
          {exercises.map(exercise => {
          const isExpanded = expandedExercise === exercise.id;
          const directUrl = getDirectUrl(exercise);
          const embedCode = getEmbedCode(exercise);
          return <div key={exercise.id} data-id="element-1655">
                {/* Exercise Row */}
                <div className="grid grid-cols-12 gap-4 p-4 hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => setExpandedExercise(isExpanded ? null : exercise.id)} data-id="element-1656">
                  <div className="col-span-4 flex items-center gap-3" data-id="element-1657">
                    {isExpanded ? <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-1658" /> : <ChevronRightIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-1659" />}
                    <div className="min-w-0" data-id="element-1660">
                      <div className="font-medium text-gray-900 truncate" data-id="element-1661">
                        {exercise.title}
                      </div>
                      <div className="text-sm text-gray-500 truncate" data-id="element-1662">
                        {exercise.exerciseId}
                      </div>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center" data-id="element-1663">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${exercise.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-1664">
                      {exercise.status === 'active' ? <CheckCircleIcon className="w-3 h-3" data-id="element-1665" /> : <XCircleIcon className="w-3 h-3" data-id="element-1666" />}
                      {exercise.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center" data-id="element-1667">
                    <div className="flex gap-1" data-id="element-1668">
                      {exercise.locales.map(locale => <span key={locale} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium" data-id="element-1669">
                          {locale}
                        </span>)}
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center" data-id="element-1670">
                    <span className="text-gray-900 font-medium" data-id="element-1671">
                      {exercise.completions.toLocaleString()}
                    </span>
                  </div>
                  <div className="col-span-2 flex items-center" data-id="element-1672">
                    <span className="text-sm text-gray-600" data-id="element-1673">
                      {formatDate(exercise.publishedAt)}
                    </span>
                  </div>
                </div>
                {/* Expanded Details */}
                {isExpanded && <div className="p-6 bg-gray-50 border-t border-gray-200 space-y-6" data-id="element-1674">
                    {/* Status Control */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1675">
                      <div className="flex items-center justify-between" data-id="element-1676">
                        <div data-id="element-1677">
                          <h3 className="text-sm font-semibold text-gray-900 mb-1" data-id="element-1678">
                            Exercise Status
                          </h3>
                          <p className="text-xs text-gray-600" data-id="element-1679">
                            {exercise.status === 'active' ? 'Exercise is currently active and accessible via the embed code and URL below.' : 'Exercise is currently inactive. Activate it to make it accessible to participants.'}
                          </p>
                        </div>
                        <button onClick={e => {
                    e.stopPropagation();
                    handleToggleStatus(exercise.id);
                  }} className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all text-sm ${exercise.status === 'active' ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' : 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200'}`} data-id="element-1680">
                          <PowerIcon className="w-4 h-4" data-id="element-1681" />
                          {exercise.status === 'active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </div>
                    {/* Access Information */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2" data-id="element-1682">
                      <EyeIcon className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1683" />
                      <div className="text-xs text-blue-800" data-id="element-1684">
                        <strong data-id="element-1685">How Participants Access This Exercise:</strong>{' '}
                        Participants can ONLY access this exercise through the
                        direct URL or embed code shown below.
                      </div>
                    </div>
                    {/* Direct URL */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1686">
                      <div className="flex items-center justify-between mb-2" data-id="element-1687">
                        <div data-id="element-1688">
                          <label className="text-xs font-semibold text-gray-900 block mb-0.5" data-id="element-1689">
                            Direct Exercise URL
                          </label>
                          <p className="text-xs text-gray-600" data-id="element-1690">
                            Share this URL directly or use it in the embed code
                            below
                          </p>
                        </div>
                        <button onClick={e => {
                    e.stopPropagation();
                    window.open(directUrl, '_blank');
                  }} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-1691">
                          <ExternalLinkIcon className="w-3 h-3" data-id="element-1692" />
                          Open
                        </button>
                      </div>
                      <div className="flex gap-2" data-id="element-1693">
                        <code className="flex-1 bg-gray-50 p-2 rounded border border-gray-200 text-xs font-mono break-all" data-id="element-1694">
                          {directUrl}
                        </code>
                        <button onClick={e => {
                    e.stopPropagation();
                    handleCopy(directUrl, 'URL');
                  }} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 transition-colors flex items-center gap-1.5 text-xs" data-id="element-1695">
                          <CopyIcon className="w-3 h-3" data-id="element-1696" />
                          Copy
                        </button>
                      </div>
                    </div>
                    {/* Embed Code */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1697">
                      <div className="flex items-center justify-between mb-2" data-id="element-1698">
                        <div data-id="element-1699">
                          <label className="text-xs font-semibold text-gray-900 block mb-0.5" data-id="element-1700">
                            Embed Code
                          </label>
                          <p className="text-xs text-gray-600" data-id="element-1701">
                            Copy this iframe code and paste it into your
                            website's HTML
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2" data-id="element-1702">
                        <pre className="flex-1 bg-gray-50 p-2 rounded border border-gray-200 text-xs font-mono overflow-x-auto" data-id="element-1703">
                          {embedCode}
                        </pre>
                        <button onClick={e => {
                    e.stopPropagation();
                    handleCopy(embedCode, 'Embed code');
                  }} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 transition-colors flex items-center gap-1.5 text-xs self-start" data-id="element-1704">
                          <CopyIcon className="w-3 h-3" data-id="element-1705" />
                          Copy
                        </button>
                      </div>
                    </div>
                    {/* Activation Token */}
                    <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1706">
                      <div className="flex items-center justify-between mb-2" data-id="element-1707">
                        <div data-id="element-1708">
                          <label className="text-xs font-semibold text-gray-900 block mb-0.5" data-id="element-1709">
                            Activation Token
                          </label>
                          <p className="text-xs text-gray-600" data-id="element-1710">
                            This token is embedded in the URL and authenticates
                            access to the exercise
                          </p>
                        </div>
                        <button onClick={e => {
                    e.stopPropagation();
                    handleRegenerateToken(exercise.id);
                  }} className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1" data-id="element-1711">
                          <RefreshCwIcon className="w-3 h-3" data-id="element-1712" />
                          Regenerate
                        </button>
                      </div>
                      <div className="flex gap-2" data-id="element-1713">
                        <code className="flex-1 bg-gray-50 p-2 rounded border border-gray-200 text-xs font-mono" data-id="element-1714">
                          {exercise.activationToken}
                        </code>
                        <button onClick={e => {
                    e.stopPropagation();
                    handleCopy(exercise.activationToken, 'Token');
                  }} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 transition-colors flex items-center gap-1.5 text-xs" data-id="element-1715">
                          <CopyIcon className="w-3 h-3" data-id="element-1716" />
                          Copy
                        </button>
                      </div>
                    </div>
                    {/* Warning about token regeneration */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2" data-id="element-1717">
                      <AlertCircleIcon className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" data-id="element-1718" />
                      <div className="text-xs text-yellow-800" data-id="element-1719">
                        <strong data-id="element-1720">Warning:</strong> Regenerating the activation
                        token will invalidate the current token. Any existing
                        embed codes will need to be updated with the new token.
                      </div>
                    </div>
                  </div>}
              </div>;
        })}
        </div>
      </div>
    </div>;
}